import { createStore } from 'vuex';
import { createEmptyPostcard } from './data/postcardOptions.js';
import { track, USD, POSTCARD_ITEM } from './utils/track';

// Price per postcard in dollars
export const POSTCARD_PRICE = 5;

function syncCartToLocalStorage(cartItems) {
    if (cartItems.length === 0) {
        localStorage.removeItem('cartItems');
    } else {
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
    }
}

export const store = createStore({
    strict: process.env.NODE_ENV !== 'production',
    state: {
        postcard: createEmptyPostcard(),
        cartItems: [],
        isCartOpen: false,
        relationships: [],
        activeRelationship: null,
        agentPostcardOptions: [],
        senderProfile: null,
    },
    mutations: {
        SET_POSTCARD(state, postcardData) {
            state.postcard = { ...state.postcard, ...postcardData };
        },
        RESET_POSTCARD(state) {
            state.postcard = createEmptyPostcard();
        },
        ADD_TO_CART(state, postcardData) {
            state.cartItems.push(postcardData);
            syncCartToLocalStorage(state.cartItems);
        },
        REMOVE_FROM_CART(state, index) {
            state.cartItems.splice(index, 1);
            syncCartToLocalStorage(state.cartItems);
        },
        RESET_CART(state) {
            state.cartItems = [];
            syncCartToLocalStorage(state.cartItems);
        },
        TOGGLE_CART(state) {
            state.isCartOpen = !state.isCartOpen;
        },
        SET_CART(state, cartItems) {
            state.cartItems = cartItems;
            syncCartToLocalStorage(state.cartItems);
        },
        SET_RELATIONSHIPS(state, relationships) {
            state.relationships = relationships;
        },
        SET_ACTIVE_RELATIONSHIP(state, relationship) {
            state.activeRelationship = relationship;
        },
        SET_AGENT_POSTCARD_OPTIONS(state, options) {
            state.agentPostcardOptions = options;
        },
        CLEAR_AGENT_POSTCARD_OPTIONS(state) {
            state.agentPostcardOptions = [];
        },
        SET_SENDER_PROFILE(state, profile) {
            state.senderProfile = profile;
        }
    },
    actions: {
        logout({ commit }) {
            commit('RESET_POSTCARD');
            commit('SET_SENDER_PROFILE', null);
            sessionStorage.removeItem('postcardData');
            localStorage.removeItem('cartItems');
        },
        savePostcard({ commit }, postcardData) {
            commit('SET_POSTCARD', postcardData);
            sessionStorage.setItem('postcardData', JSON.stringify(postcardData));
        },
        clearPostcard({ commit }) {
            commit('RESET_POSTCARD');
            sessionStorage.removeItem('postcardData');
        },
        initializePostcard({ commit }) {
            try {
                const storedPostcard = JSON.parse(sessionStorage.getItem('postcardData'));
                if (storedPostcard) {
                    commit('SET_POSTCARD', storedPostcard);
                }
            } catch (e) {
                sessionStorage.removeItem('postcardData');
            }
        },
        addToCart({ commit }, postcardData) {
            commit('ADD_TO_CART', { ...postcardData, price: POSTCARD_PRICE });
            track('add_to_cart', {
                currency: USD,
                value: POSTCARD_PRICE,
                items: [{ ...POSTCARD_ITEM, price: POSTCARD_PRICE, quantity: 1 }],
            });
        },
        removeFromCart({ commit }, index) {
            commit('REMOVE_FROM_CART', index);
        },
        clearCart({ commit }) {
            commit('RESET_CART');
        },
        toggleCart({ commit }) {
            commit('TOGGLE_CART');
        },
        initializeCart({ commit }) {
            try {
                const storedCart = JSON.parse(localStorage.getItem('cartItems'));
                if (storedCart) {
                    commit('SET_CART', storedCart);
                }
            } catch (e) {
                localStorage.removeItem('cartItems');
            }
        },
        setRelationships({ commit }, relationships) {
            commit('SET_RELATIONSHIPS', relationships);
        },
        setActiveRelationship({ commit }, relationship) {
            commit('SET_ACTIVE_RELATIONSHIP', relationship);
        },
        setAgentPostcardOptions({ commit }, options) {
            commit('SET_AGENT_POSTCARD_OPTIONS', options);
        },
        clearAgentPostcardOptions({ commit }) {
            commit('CLEAR_AGENT_POSTCARD_OPTIONS');
        },
        setSenderProfile({ commit }, profile) {
            commit('SET_SENDER_PROFILE', profile);
        }
    },
    getters: {
        getPostcard(state) {
            return state.postcard;
        },
        cartItemCount(state) {
            return state.cartItems.length;
        },
        cartItems(state) {
            return state.cartItems;
        },
        isCartOpen(state) {
            return state.isCartOpen;
        },
        totalPrice(state) {
            return state.cartItems.reduce((total, item) => total + item.price, 0);
        },
        itemPrice() {
            return POSTCARD_PRICE;
        },
        relationships(state) {
            return state.relationships;
        },
        activeRelationship(state) {
            return state.activeRelationship;
        },
        agentPostcardOptions(state) {
            return state.agentPostcardOptions;
        },
        senderProfile(state) {
            return state.senderProfile;
        },
    }
});
